import React, { useEffect, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
} from "react-router-dom";
import "@/App.css";
import { I18nProvider } from "./context/I18nContext";
import { LANGS, DEFAULT_LANG, PAGE_SLUGS } from "./lib/routes";
import Header from "./components/site/Header";
import Footer from "./components/site/Footer";
import CookieBanner from "./components/site/CookieBanner";
import ScrollToTop from "./components/site/ScrollToTop";
import HashScroller from "./components/site/HashScroller";
import HomePage from "./pages/HomePage";
import PolicyPage from "./pages/PolicyPage";
import ThankYouPage from "./pages/ThankYouPage";
import { initConsentDefaults, readStoredConsent, updateConsent } from "./lib/consent";

const SiteShell = ({ lang, children }) => {
  const [cookieOpenSignal, setCookieOpenSignal] = useState(0);
  return (
    <I18nProvider lang={lang}>
      <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:bg-white focus:px-3 focus:py-2 focus:rounded"
        >
          Skip to main content
        </a>
        <Header />
        <div className="flex-1">{children}</div>
        <Footer onOpenCookiePrefs={() => setCookieOpenSignal((v) => v + 1)} />
        <CookieBanner openSignal={cookieOpenSignal} />
      </div>
    </I18nProvider>
  );
};

const LangHome = () => {
  const { lang } = useParams();
  const l = LANGS.includes(lang) ? lang : DEFAULT_LANG;
  return (
    <SiteShell lang={l}>
      <HomePage />
    </SiteShell>
  );
};

const LangPolicy = ({ policyKey }) => {
  const { lang } = useParams();
  const l = LANGS.includes(lang) ? lang : DEFAULT_LANG;
  return (
    <SiteShell lang={l}>
      <PolicyPage policyKey={policyKey} />
    </SiteShell>
  );
};

const ThankYouShell = () => (
  <I18nProvider lang="ec">
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <div className="flex-1">
        <ThankYouPage />
      </div>
      <Footer />
    </div>
  </I18nProvider>
);

const NotFound = () => {
  const location = useLocation();
  // Redirect to default language home
  return <Navigate to={`/${DEFAULT_LANG}`} replace state={{ from: location.pathname }} />;
};

function App() {
  useEffect(() => {
    // Initialise Google Consent Mode v2 defaults BEFORE any tags fire.
    initConsentDefaults();
    // Apply stored consent if user already decided in a previous session.
    const stored = readStoredConsent();
    if (stored) updateConsent(stored);
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <HashScroller />
        <Routes>
          <Route path="/" element={<Navigate to={`/${DEFAULT_LANG}`} replace />} />
          <Route path="/thank_you" element={<ThankYouShell />} />

          {/* Language index */}
          <Route path="/:lang" element={<LangHome />} />

          {/* Spanish policy slugs */}
          <Route
            path={`/:lang/${PAGE_SLUGS.privacy.ec}`}
            element={<LangPolicy policyKey="privacy" />}
          />
          <Route
            path={`/:lang/${PAGE_SLUGS.cookies.ec}`}
            element={<LangPolicy policyKey="cookies" />}
          />
          <Route
            path={`/:lang/${PAGE_SLUGS.terms.ec}`}
            element={<LangPolicy policyKey="terms" />}
          />

          {/* English policy slugs */}
          <Route
            path={`/:lang/${PAGE_SLUGS.privacy.en}`}
            element={<LangPolicy policyKey="privacy" />}
          />
          <Route
            path={`/:lang/${PAGE_SLUGS.cookies.en}`}
            element={<LangPolicy policyKey="cookies" />}
          />
          <Route
            path={`/:lang/${PAGE_SLUGS.terms.en}`}
            element={<LangPolicy policyKey="terms" />}
          />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
